import * as React from 'react';
import {
    BoxSkeleton,
    HorizontalDivider,
    LinkButton,
    SkeletonLoader,
    useFullLayout,
    useL2DTheme,
    FlexyIconButton,
} from '@europrocurement/flexy-components';
import {
    DossierCustomHeaderHeaderActionsBloc,
    DossierRoutingContext,
    DossierRoutingProvider,
    dossiersBasePath,
    DossierSmallCard,
    dossierValidationPath,
    ordersBasePath,
    useCurrentDossierCtx,
    useDossierCustomHeader,
    useDossierDocumentsCtx,
    useLazyDataLoader,
} from '@europrocurement/l2d-modules';
import { ClientReference } from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DetailDossierTitle/fragments/ClientReference';
import { HeaderTitle } from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DetailDossierTitle/fragments/HeaderTitle';
import { PrescripteurOffreSection } from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DetailDossierTitle/fragments/PrescripteurOffreSection';
import { useTiersCtx } from '@europrocurement/l2d-modules/providers/TiersProvider';
import { Box, SxProps, Typography } from '@mui/material';
import { useEditSousClient } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import { editIcon } from '@europrocurement/l2d-icons';

import { prescripteursBasePath } from '../../../Prescripteurs/constants/paths';
import { addFormalityPath } from '../../constants';

type DetailDossierLeftNavBarProps = {
    sx?: SxProps;
};
const DetailDossierLeftNavBar: React.FunctionComponent<DetailDossierLeftNavBarProps> = function ({
    sx,
}) {
    const { palette } = useL2DTheme();
    const { setProfileHeaderVisible, setPageTitle, clearPageTitle } = useFullLayout();
    const { siblings, dossier } = useCurrentDossierCtx();
    const { getOrderRoute, getDossierRoute } = React.useContext(DossierRoutingContext);
    const { isFetch, getTiersById } = useTiersCtx();
    const { dosDocsloaded, getDossierDocuments } = useDossierDocumentsCtx();
    const { loadTiers, refreshTiers } = useLazyDataLoader();
    const { setActionsBloc, setHeaderTitle } = useDossierCustomHeader();

    const clientOpere = React.useMemo(
        () => (dossier?.clientOpereId ? getTiersById(dossier?.clientOpereId) : undefined),
        [dossier?.clientOpereId, getTiersById],
    );

    const prescripteur = React.useMemo(() => {
        if (!dossier?.prescripteurId || !isFetch(dossier.prescripteurId)) return undefined;
        const prescriber = getTiersById(dossier.prescripteurId);
        if (!prescriber) return undefined;

        return {
            ...prescriber,
            id: prescriber?.id as number,
            raisonSociale: (prescriber?.raisonSociale as string) || 'Pas de raison sociale',
            codeComptable: prescriber?.codeComptable || 'Pas de code comptable',
            /** URL vers la page de détail du prescripteur */
            urlToDetails: `/${prescripteursBasePath}/liste/${dossier.prescripteurId}`,
        };
    }, [dossier?.prescripteurId, getTiersById, isFetch]);

    const formattedOffre = React.useMemo(
        () => ({
            libelle: dossier?.offreLibelle || 'Aucune offre',
        }),
        [dossier?.offreLibelle],
    );

    // Update custom Header data, the actions will be set in the DetailDossierLeftNavBar
    React.useEffect(() => {
        if (dossier) {
            setProfileHeaderVisible(false);
            setPageTitle(`Dossier #${dossier?.numero} | ${clientOpere?.raisonSociale}`);
        }
        return () => {
            setProfileHeaderVisible(true);
            clearPageTitle();
        };
    }, [
        clearPageTitle,
        clientOpere?.raisonSociale,
        dossier,
        setPageTitle,
        setProfileHeaderVisible,
    ]);

    React.useEffect(() => {
        const allIds: number[] = [];
        [dossier?.prescripteurId, dossier?.clientOpereId, dossier?.clientId]
            .filter((id) => id !== undefined)
            .reduce((accumulator, currentId: number) => {
                if (!accumulator.includes(currentId)) {
                    accumulator.push(currentId);
                }

                return accumulator;
            }, allIds)
            .map((tiersId) => loadTiers(tiersId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.prescripteurId, dossier?.clientOpereId, dossier?.clientId]);

    React.useEffect(() => {
        if (dossier && siblings && prescripteur) {
            setActionsBloc(
                <DossierRoutingProvider
                    orderBasePath={ordersBasePath}
                    dossierBasePath={dossiersBasePath}
                    dossierValidationPath={dossierValidationPath}
                    addFormalityPath={addFormalityPath}
                >
                    <DossierCustomHeaderHeaderActionsBloc
                        dossier={dossier}
                        siblings={siblings}
                        prescripteur={prescripteur}
                    />
                    ,
                </DossierRoutingProvider>,
            );
        }
    }, [
        dossier,
        getDossierDocuments,
        prescripteur,
        setActionsBloc,
        setHeaderTitle,
        siblings,
        dosDocsloaded,
    ]);
    const { editFolio } = useEditSousClient({
        sousClientId: clientOpere?.id,
        onSuccess: () => refreshTiers(clientOpere?.id as number),
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                overflowX: 'hidden',
                overflowY: 'hidden',
                gap: '8px',
                // 100 vh screen - 5vh footer - 72px header page
                maxHeight: 'calc(95vh - 72px)',
                ...sx,
            }}
        >
            {/* Collection Card  */}
            <Box sx={{ flexShrink: 0 }}>
                <SkeletonLoader
                    isLoading={!dossier || !clientOpere}
                    skeleton={
                        <BoxSkeleton
                            sx={{ height: '300px', marginTop: '24px', borderRadius: '8px' }}
                        />
                    }
                >
                    <Box
                        data-testid="dossier_small_card"
                        sx={{
                            backgroundColor: palette.background.paper,
                            borderRadius: '8px',
                            padding: '14px',
                            border: `1px solid ${palette.grey[200]} `,
                            gap: '8px',
                            display: 'flex',
                            marginTop: '24px',
                            flexDirection: 'column',
                            boxShadow: `0px 2px 2.5px 0px ${palette.grey[200]}`,
                        }}
                    >
                        {dossier && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <LinkButton
                                    to={getOrderRoute(dossier.collectionId as number)}
                                    onClick={() => {}}
                                    wrapperSx={{
                                        backgroundColor: palette.grey[100],
                                        '&:hover': {
                                            backgroundColor: palette.grey[200],
                                            color: palette.grey[900],
                                        },
                                        color: palette.grey[700],
                                        padding: '6px 8px',
                                    }}
                                    textSx={{ fontSize: '0.8rem' }}
                                    label={`Commande #${dossier.collectionNumero as string}`}
                                />
                            </Box>
                        )}
                        {clientOpere && (
                            <>
                                <Box>
                                    <HeaderTitle
                                        clientOpereRaisonSociale={clientOpere.raisonSociale}
                                        prescriberId={prescripteur?.id}
                                        sousClientId={clientOpere.id}
                                        onSuccess={() => refreshTiers(clientOpere.id)}
                                    />
                                    <Typography variant="l2dbodyxs">
                                        {clientOpere?.adresse?.fullAddress as string}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <Typography
                                        variant="l2dbodys"
                                        color={palette.text.primary}
                                    >
                                        SIREN:
                                    </Typography>
                                    {clientOpere.siren ? (
                                        <Box sx={{ display: 'flex' }}>
                                            <LinkButton
                                                to={`https://www.societe.com/cgi-bin/fiche?rncs=${clientOpere.siren}`}
                                                target="_blank"
                                                onClick={() => {}}
                                                wrapperSx={{
                                                    backgroundColor: palette.grey[100],
                                                    '&:hover': {
                                                        backgroundColor: palette.grey[200],
                                                        color: palette.grey[900],
                                                    },
                                                    fontWeight: 500,
                                                    color: palette.grey[700],
                                                    padding: '6px 8px',
                                                }}
                                                label={clientOpere.siren}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="l2dbodys"
                                            sx={{ fontWeight: 500, color: palette.grey[700] }}
                                        >
                                            À renseigner
                                        </Typography>
                                    )}
                                    {!(
                                        prescripteur?.id !== undefined &&
                                        prescripteur?.id === clientOpere.id
                                    ) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: '4px',
                                                flexShrink: 0,
                                                opacity: 0,
                                                transform: 'translateX(-10px)',
                                                transition:
                                                    'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                                '&:hover': {
                                                    opacity: 1,
                                                    transform: 'translateX(0)',
                                                },
                                            }}
                                        >
                                            <FlexyIconButton
                                                displayTooltip
                                                icon={editIcon}
                                                onClick={editFolio}
                                                tooltipOverwriteProps={{
                                                    title: 'Éditer le siren de la société',
                                                }}
                                                iconOverwriteProps={{
                                                    size: '2xs',
                                                    sx: {
                                                        '&:hover': {
                                                            color: 'primary.main',
                                                        },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}
                        {dossier && <ClientReference societeRef={dossier.societeRef as string} />}

                        {/* Prescripteur et offre */}
                        {prescripteur && (
                            <PrescripteurOffreSection
                                prescripteur={prescripteur}
                                offre={formattedOffre}
                                palette={palette}
                                source="BACK"
                            />
                        )}
                    </Box>
                </SkeletonLoader>
            </Box>

            {/* Dossiers Card  */}
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <SkeletonLoader
                    isLoading={!siblings || !dossier}
                    skeleton={<BoxSkeleton sx={{ height: '200px', borderRadius: '8px' }} />}
                >
                    <Box>
                        {dossier && siblings && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <HorizontalDivider />

                                <Typography
                                    variant="l2dbodym"
                                    sx={{ color: palette.grey[700], paddingLeft: '10px' }}
                                >
                                    Dossiers ({siblings.length})
                                </Typography>

                                {siblings
                                    .sort((a, b) => Number(a.deleted) - Number(b.deleted))
                                    .map((doss) => (
                                        <DossierSmallCard
                                            key={doss.id}
                                            dossier={doss}
                                            isCurrentDossier={doss.id === dossier.id}
                                            link={getDossierRoute(doss.id as number)}
                                        />
                                    ))}
                            </Box>
                        )}
                    </Box>
                </SkeletonLoader>
            </Box>
        </Box>
    );
};

export default DetailDossierLeftNavBar;
